/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */

import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity } from 'react-native';
import { format } from 'date-fns';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import { renderContentWithMentions } from './mentionUtils';
import DeleteCommentModal from './DeleteCommentModal';
import MentionDropdown, {
  MENTION_DROPDOWN_MAX_HEIGHT,
  MENTION_DROPDOWN_OFFSET,
} from './MentionDropdown';
import useCommentMentions from './useCommentMentions';

const EDIT_LINE_HEIGHT = 20;
const MAX_EDIT_VISIBLE_LINES = 2;
const EDIT_PADDING = 8;
const EDIT_BORDER_WIDTH = 1;
const MAX_EDIT_HEIGHT =
  EDIT_LINE_HEIGHT * MAX_EDIT_VISIBLE_LINES +
  EDIT_PADDING * 2 +
  EDIT_BORDER_WIDTH * 2;
const MAX_EDIT_OVERLAY_HEIGHT = MAX_EDIT_HEIGHT - EDIT_BORDER_WIDTH * 2;

const MessageItem = ({
  comment,
  isOwn,
  onEdit,
  onDelete,
  onOpenManage,
  getListRect,
  isEditing,
  canManage,
  onEditingChange,
}) => {
  const [editValue, setEditValue] = useState(comment.content);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editScrollOffset, setEditScrollOffset] = useState(0);
  const menuButtonRef = useRef(null);
  const editInputRef = useRef(null);
  const editWrapperRef = useRef(null);
  const [mentionPlacement, setMentionPlacement] = useState('top');
  const [mentionMaxHeight, setMentionMaxHeight] = useState(
    MENTION_DROPDOWN_MAX_HEIGHT,
  );

  // [B2B-5934] Same "@" + 3 chars tagging as the new-comment box (CommentInput), seeded with
  // the comment's existing mentions so they stay highlighted and are kept on save.
  const {
    mentions,
    mentionUsers,
    activeMentionIndex,
    mentionHasMore,
    onLoadMoreMentions,
    onMentionTextChange,
    onMentionKeyPress,
    onSelectMention,
    clearMentionUsers,
    getUsedMentions,
    resetMentions,
  } = useCommentMentions({
    content: editValue,
    setContent: setEditValue,
    inputRef: editInputRef,
    initialMentions: comment.mentions || [],
  });

  const openManageSheet = () => {
    if (menuButtonRef.current?.measureInWindow) {
      menuButtonRef.current.measureInWindow((x, y, width, height) => {
        onOpenManage({
          anchorRect: { x, y, width, height },
          onEdit: () => {
            setEditValue(comment.content);
            resetMentions(comment.mentions || []);
            onEditingChange(true);
          },
          onDelete: () => setShowDeleteModal(true),
        });
      });
    }
  };

  // The edit box sits inside the scrollable comment list, so an upward dropdown gets clipped
  // for comments near the top. Open it upward when it fits, otherwise toward the larger side.
  const hasMentionUsers = mentionUsers.length > 0;
  useEffect(() => {
    if (!hasMentionUsers || !getListRect || !editWrapperRef.current) {
      return;
    }
    editWrapperRef.current.measureInWindow((x, y, width, height) => {
      getListRect((listRect) => {
        // Bail out on an unusable measurement (keeps the defaults) instead of
        // feeding NaN into the dropdown's maxHeight.
        if (
          !listRect ||
          ![y, height, listRect.top, listRect.bottom].every(Number.isFinite)
        ) {
          return;
        }
        const needed = MENTION_DROPDOWN_MAX_HEIGHT + MENTION_DROPDOWN_OFFSET;
        const spaceAbove = y - listRect.top;
        const spaceBelow = listRect.bottom - (y + height);
        const placement =
          spaceAbove >= needed || spaceAbove >= spaceBelow ? 'top' : 'bottom';
        // Neither side may fit the full dropdown (e.g. a comment near the top of a short
        // list), so cap it to the room on the chosen side; its list scrolls for the rest.
        const room =
          (placement === 'top' ? spaceAbove : spaceBelow) -
          MENTION_DROPDOWN_OFFSET;
        setMentionPlacement(placement);
        setMentionMaxHeight(
          Math.min(MENTION_DROPDOWN_MAX_HEIGHT, Math.max(room, 0)),
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMentionUsers]);

  const onSaveEdit = () => {
    if (!editValue.trim()) {
      return;
    }
    onEdit(comment._id, editValue.trim(), getUsedMentions(editValue));
    onEditingChange(false);
  };

  const syncEditScrollOffset = () => {
    requestAnimationFrame(() => {
      const node = editInputRef.current;
      if (node && typeof node.scrollTop === 'number') {
        setEditScrollOffset(node.scrollTop);
      }
    });
  };

  const onChangeEditText = (value) => {
    setEditValue(value);
    onMentionTextChange(value);
    syncEditScrollOffset();
  };

  return (
    <View
      testID={`comment-item-${comment._id}`}
      style={{
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: Colors.grey0,
        // [B2B-5934] Each list row is its own stacking layer (RN-web Views default to
        // zIndex 0; native draws later siblings on top), so a downward mention dropdown
        // gets covered by the comments below. Lift this row while the dropdown is open.
        ...(hasMentionUsers ? { zIndex: 1 } : {}),
      }}>
      <View style={{ flexDirection: 'row' }}>
        <View
          style={{
            width: 26,
            height: 26,
            borderRadius: 13,
            backgroundColor: Colors.blue0,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 10,
          }}>
          <SociollaText
            style={{ color: Colors.white, fontSize: 10 }}
            type="bold">
            {comment.user_initials}
          </SociollaText>
        </View>
        <View style={{ flex: 1 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <SociollaText type="bold" style={{ fontSize: 14 }}>
                {comment.user_name}
              </SociollaText>
              <SociollaText
                style={{ fontSize: 12, color: Colors.grey1, marginLeft: 8 }}>
                {format(new Date(comment.created_at), 'dd MMM yyyy, HH:mm')}
              </SociollaText>
            </View>
            {isOwn && canManage ? (
              <TouchableOpacity
                ref={menuButtonRef}
                testID={`comment-manage-${comment._id}`}
                onPress={openManageSheet}
                style={{ paddingHorizontal: 8, paddingVertical: 4 }}>
                <Image
                  source={require('@assets/img/icons/more-vertical-grey.svg')}
                  style={{ width: 4, height: 16 }}
                  width={4}
                  height={16}
                  svg={true}
                />
              </TouchableOpacity>
            ) : (
              false
            )}
          </View>

          {isEditing ? (
            <View style={{ marginTop: 6 }}>
              {/* Anchors the dropdown to the input box itself (not the whole edit block),
                  so 'bottom' opens right under the input; zIndex keeps it above the buttons. */}
              <View
                ref={editWrapperRef}
                collapsable={false}
                style={{ position: 'relative', zIndex: 1 }}>
                <MentionDropdown
                  placement={mentionPlacement}
                  maxHeight={mentionMaxHeight}
                  users={mentionUsers}
                  activeIndex={activeMentionIndex}
                  onSelect={onSelectMention}
                  hasMore={mentionHasMore}
                  onLoadMore={onLoadMoreMentions}
                />
                {/* Colored overlay so @mentions stay blue while editing, same technique as
                  CommentInput's compose box — the real TextInput's text is made transparent
                  and this sits behind it, insetted by the same border+padding box model. */}
                <View
                  pointerEvents="none"
                  style={{
                    position: 'absolute',
                    top: 1,
                    left: 1,
                    right: 1,
                    height: MAX_EDIT_OVERLAY_HEIGHT,
                    overflow: 'hidden',
                    padding: 8,
                  }}>
                  <Text
                    style={[
                      {
                        fontFamily: 'figtree-regular',
                        fontSize: 14,
                        lineHeight: EDIT_LINE_HEIGHT,
                      },
                      { transform: [{ translateY: -editScrollOffset }] },
                    ]}>
                    {renderContentWithMentions(editValue, mentions)}
                  </Text>
                </View>
                <TextInput
                  testID={`comment-edit-input-${comment._id}`}
                  ref={editInputRef}
                  value={editValue}
                  onChangeText={onChangeEditText}
                  onKeyPress={onMentionKeyPress}
                  onSelectionChange={syncEditScrollOffset}
                  multiline
                  textAlignVertical="top"
                  cursorColor={Colors.black}
                  onScroll={(e) =>
                    setEditScrollOffset(e.nativeEvent.contentOffset.y)
                  }
                  style={{
                    fontFamily: 'figtree-regular',
                    fontSize: 14,
                    lineHeight: EDIT_LINE_HEIGHT,
                    height: MAX_EDIT_HEIGHT,
                    borderWidth: 1,
                    borderColor: Colors.blue0,
                    borderRadius: 6,
                    padding: 8,
                    color: Colors.transparent,
                    caretColor: Colors.black,
                  }}
                />
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  marginTop: 6,
                }}>
                <TouchableOpacity
                  testID={`comment-edit-cancel-${comment._id}`}
                  onPress={() => {
                    clearMentionUsers();
                    onEditingChange(false);
                  }}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 12,
                    borderWidth: 1,
                    borderColor: Colors.grey0,
                    borderRadius: 6,
                    marginRight: 8,
                  }}>
                  <SociollaText style={{ fontSize: 13 }}>Cancel</SociollaText>
                </TouchableOpacity>
                <TouchableOpacity
                  testID={`comment-edit-save-${comment._id}`}
                  onPress={onSaveEdit}
                  style={{
                    paddingVertical: 6,
                    paddingHorizontal: 12,
                    backgroundColor: Colors.blue0,
                    borderRadius: 6,
                  }}>
                  <SociollaText style={{ fontSize: 13, color: Colors.white }}>
                    Save
                  </SociollaText>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <Text
              style={{
                fontFamily: 'figtree-regular',
                color: Colors.black1,
                fontSize: 14,
                marginTop: 4,
                lineHeight: 20,
                whiteSpace: 'pre-line',
              }}>
              {renderContentWithMentions(comment.content, comment.mentions)}
            </Text>
          )}
        </View>
      </View>

      <DeleteCommentModal
        visible={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          setShowDeleteModal(false);
          onDelete(comment._id);
        }}
      />
    </View>
  );
};

export default MessageItem;
